import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Eye, Building2, Pencil, TrendingUp, FileBarChart, Globe, Phone, Mail, FileCheck2, FileText, Wallet, Trash2, Receipt } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../components/ui/use-toast';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RecordPaymentModal from '../../../components/admin/finance/RecordPaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  issued: { label: 'Issued', color: 'bg-blue-100 text-blue-800' },
  proforma: { label: 'Pro-Forma', color: 'bg-amber-100 text-amber-800' },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-800' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-800' },
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800' }
};

// Mirrors backend tax_id_labels.py — keeps the client tax ID label correct
// (Angolan client registered with NIF_AO renders as "NIF", not "TRN").
const TAX_ID_SHORT_LABEL = {
  TRN_UAE: 'TRN', TRADE_LICENSE_UAE: 'LIC',
  NIF_AO: 'NIF', NIF_PT: 'NIF', NIF_ES: 'NIF',
  CNPJ_BR: 'CNPJ', EIN_US: 'EIN', VAT_UK: 'VAT', VAT_EU: 'VAT',
  SIREN_FR: 'SIREN', PIVA_IT: 'P.IVA', STEUER_DE: 'StID',
  TIN_ZA: 'VAT', TIN_KE: 'PIN', TIN_NG: 'TIN', TIN_MZ: 'NUIT',
  CR_CN: 'USCC', GST_IN: 'GSTIN', BN_CA: 'BN', ABN_AU: 'ABN',
  OTHER: 'Tax ID',
};
const taxLabelFor = (key, fallback = 'Tax ID') => TAX_ID_SHORT_LABEL[key] || fallback;

const FinanceInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  const [issuing, setIssuing] = useState(null); // 'proforma' | 'invoice' | null
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  useEffect(() => { fetchInvoice(); fetchPayments(); }, [id]);

  const fetchInvoice = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/finance/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoice(response.data);
    } catch (error) {
      toast({ variant: 'destructive', title: 'Error', description: 'Failed to load invoice' });
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    setPaymentsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/finance/invoices/${id}/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayments(r.data || []);
    } catch (_) {
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(
      `Reverse installment of ${formatCurrency(payment.amount, invoice.currency)} ` +
      `dated ${formatDate(payment.payment_date)}?\n\n` +
      `The invoice balance, paid amount and status will be recalculated automatically.\n\n` +
      `This action cannot be undone.`
    )) return;
    try {
      await axios.delete(`${API_URL}/api/finance/payments/${payment.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({ title: 'Installment reversed', description: 'Invoice balance recalculated.' });
      await fetchInvoice();
      await fetchPayments();
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Reversal failed',
        description: e.response?.data?.detail || 'Could not delete installment.',
      });
    }
  };

  const handlePreviewPDF = () => {
    window.open(`${API_URL}/api/finance/invoices/${id}/pdf/preview?token=${token}`, '_blank');
  };

  const handleDownloadPDF = () => {
    window.open(`${API_URL}/api/finance/invoices/${id}/pdf/download?token=${token}`, '_blank');
  };

  const handlePreviewCostMargin = () => {
    window.open(`${API_URL}/api/finance/invoices/${id}/cost-margin-pdf/preview?token=${token}`, '_blank');
  };

  const handleDownloadCostMargin = () => {
    window.open(`${API_URL}/api/finance/invoices/${id}/cost-margin-pdf/download?token=${token}`, '_blank');
  };

  const handleConvertToTaxInvoice = async () => {
    if (!window.confirm(
      'Convert this Pro-Forma into a final TAX INVOICE?\n\n' +
      'A new invoice will be issued with a permanent AE-…-INV-… number, ' +
      'using the SAME exchange rate frozen on this pro-forma. The pro-forma ' +
      'remains in the system for audit purposes and cannot be converted again.'
    )) return;
    setConverting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/finance/invoices/${id}/convert-to-tax-invoice`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.success) {
        toast({
          title: 'Tax Invoice issued',
          description: `${r.data.invoice_number} created from ${r.data.source_proforma_number}.`,
        });
        navigate(`/admin/finance/invoices/${r.data.invoice_id}`);
      }
    } catch (e) {
      toast({
        variant: 'destructive',
        title: 'Conversion failed',
        description: e.response?.data?.detail || 'Could not convert pro-forma.',
      });
    } finally {
      setConverting(false);
    }
  };

  const handleIssueDraft = async (asType) => {
    const label = asType === 'proforma' ? 'Pro-Forma' : 'Tax Invoice';
    if (!window.confirm(
      `Issue this draft as ${label}?\n\n` +
      `A permanent AE-…-${asType === 'proforma' ? 'PI' : 'INV'}-… number will be assigned ` +
      `and the linked Opportunity / Deal pipeline will advance automatically.`
    )) return;
    setIssuing(asType);
    try {
      const r = await axios.post(
        `${API_URL}/api/finance/invoices/${id}/issue`,
        { as: asType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.data?.success) {
        toast({
          title: `${label} issued`,
          description: `Document number: ${r.data.invoice_number}`,
        });
        await fetchInvoice();
      }
    } catch (e) {
      toast({
        variant: 'destructive',
        title: `${label} issue failed`,
        description: e.response?.data?.detail || 'Could not issue document.',
      });
    } finally {
      setIssuing(null);
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'AED' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-[#6C757D]">Loading invoice...</div>;
  if (!invoice) return <div className="flex items-center justify-center h-64 text-[#6C757D]">Invoice not found</div>;

  const statusConfig = STATUS_CONFIG[invoice.status] || STATUS_CONFIG.draft;
  const company = invoice.company_snapshot || {};
  const client = invoice.client_snapshot || {};
  const isProforma = (invoice.invoice_type || 'invoice') === 'proforma';
  const isDraft = (invoice.status || '').toLowerCase() === 'draft';
  const alreadyConverted = !!invoice.converted_to_invoice_id;

  return (
    <div className="p-6" data-testid="finance-invoice-detail-page">
      <div className="mb-6">
        <button onClick={() => navigate('/admin/finance/invoices')}
          className="flex items-center text-[#6C757D] hover:text-[#2B2D42] mb-4" data-testid="back-to-invoices">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Invoices
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-[#2B2D42] mb-2" data-testid="invoice-number-header">
              {isProforma ? 'Pro-Forma' : 'Invoice'} {invoice.invoice_number}
            </h2>
            <div className="flex items-center space-x-4">
              <Badge className={statusConfig.color} data-testid="invoice-status-badge">{statusConfig.label}</Badge>
              {isProforma && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="invoice-type-badge">
                  PRO-FORMA
                </Badge>
              )}
              {alreadyConverted && (
                <button
                  type="button"
                  className="text-xs underline text-[#0EA5E9] hover:text-[#023E8A]"
                  onClick={() => navigate(`/admin/finance/invoices/${invoice.converted_to_invoice_id}`)}
                  data-testid="converted-link"
                >
                  → converted to {invoice.converted_to_invoice_number}
                </button>
              )}
              {invoice.source_proforma_id && (
                <button
                  type="button"
                  className="text-xs underline text-[#6C757D] hover:text-[#2B2D42]"
                  onClick={() => navigate(`/admin/finance/invoices/${invoice.source_proforma_id}`)}
                  data-testid="source-proforma-link"
                >
                  ← from pro-forma {invoice.source_proforma_number}
                </button>
              )}
              <span className="text-sm text-[#6C757D]">Created {formatDate(invoice.created_at)}</span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            {isDraft && (
              <>
                <Button
                  onClick={() => handleIssueDraft('proforma')}
                  disabled={issuing !== null}
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  data-testid="issue-as-proforma-btn"
                  title="Issue this draft as a Pro-Forma Invoice (not a fiscal document)"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  {issuing === 'proforma' ? 'Issuing…' : 'Issue as Pro-Forma'}
                </Button>
                <Button
                  onClick={() => handleIssueDraft('invoice')}
                  disabled={issuing !== null}
                  className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
                  data-testid="issue-as-tax-invoice-btn"
                  title="Issue this draft as a final Tax Invoice"
                >
                  <FileCheck2 className="h-4 w-4 mr-2" />
                  {issuing === 'invoice' ? 'Issuing…' : 'Issue as Tax Invoice'}
                </Button>
              </>
            )}
            {isProforma && !alreadyConverted && (
              <Button
                onClick={handleConvertToTaxInvoice}
                disabled={converting}
                className="bg-[#16A34A] hover:bg-[#15803D] text-white"
                data-testid="convert-to-tax-invoice-btn"
                title="Issue final TAX INVOICE — copies all fields and freezes the FX rate"
              >
                <FileCheck2 className="h-4 w-4 mr-2" />
                {converting ? 'Converting…' : 'Convert to Tax Invoice'}
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate(`/admin/finance/invoices/${id}/edit`)} data-testid="edit-invoice-btn">
              <Pencil className="h-4 w-4 mr-2" /> Editar
            </Button>
            <Button variant="outline" onClick={handlePreviewPDF} data-testid="preview-pdf-btn">
              <Eye className="h-4 w-4 mr-2" /> Preview PDF
            </Button>
            <Button variant="outline" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Parties - UAE Compliant */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 gap-6">
                <div data-testid="issuer-info">
                  <h3 className="text-sm font-semibold text-[#2B2D42] mb-3 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#FF6B35]" /> From (Issuer)
                  </h3>
                  <div className="text-sm text-[#6C757D] space-y-1">
                    <div className="font-medium text-[#2B2D42] text-base">
                      {company.legal_name || company.trading_name || 'N/A'}
                    </div>
                    {company.address && <div>{company.address}</div>}
                    <div className="font-medium text-[#2B2D42]">{company.country || 'United Arab Emirates'}</div>
                    {company.license_number && (
                      <div className="mt-2" data-testid="issuer-license">
                        <span className="font-semibold">License:</span> {company.license_number}
                      </div>
                    )}
                    {company.trn && (
                      <div data-testid="issuer-trn">
                        <span className="font-semibold">TRN:</span> {company.trn}
                      </div>
                    )}
                    {company.phone && <div>Phone: {company.phone}</div>}
                    {company.email && (
                      <div>
                        <a
                          href={`mailto:${company.email}`}
                          className="inline-flex items-center gap-1 text-[#023E8A] hover:text-[#00B4D8] hover:underline"
                          data-testid="issuer-email-link"
                        >
                          <Mail className="w-3 h-3" /> {company.email}
                        </a>
                      </div>
                    )}
                    {company.website && (
                      <div>
                        <a
                          href={/^https?:\/\//i.test(company.website) ? company.website : `https://${company.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#023E8A] hover:text-[#00B4D8] hover:underline font-medium"
                          data-testid="issuer-website-link"
                        >
                          <Globe className="w-3 h-3" /> Visit website
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <div data-testid="client-info">
                  <h3 className="text-sm font-semibold text-[#2B2D42] mb-3">To (Client)</h3>
                  <div className="text-sm text-[#6C757D] space-y-1">
                    <div className="font-medium text-[#2B2D42] text-base">
                      {client.name || invoice.client_name || 'N/A'}
                    </div>
                    <div>{client.email || invoice.client_email || 'N/A'}</div>
                    {(client.address || invoice.client_address) && <div>{client.address || invoice.client_address}</div>}
                    {(client.trn || invoice.client_trn) && (
                      <div data-testid="client-trn">
                        <span className="font-semibold">
                          {taxLabelFor(client.tax_id_type || invoice.client_tax_id_type, 'TRN')}:
                        </span>{' '}
                        {client.trn || invoice.client_trn}
                      </div>
                    )}
                    {(client.phone || invoice.client_phone) && (
                      <div className="inline-flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {client.phone || invoice.client_phone}
                      </div>
                    )}
                    {(client.website || invoice.client_website) && (
                      <div>
                        <a
                          href={(() => {
                            const w = client.website || invoice.client_website;
                            return /^https?:\/\//i.test(w) ? w : `https://${w}`;
                          })()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[#023E8A] hover:text-[#00B4D8] hover:underline font-medium"
                          data-testid="client-website-link"
                        >
                          <Globe className="w-3 h-3" /> Visit website
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Invoice Details */}
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div>
                  <p className="text-xs text-[#6C757D]">Issue Date</p>
                  <p className="font-medium text-[#2B2D42]">{formatDate(invoice.issue_date || invoice.invoice_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6C757D]">Due Date</p>
                  <p className="font-medium text-[#2B2D42]">{formatDate(invoice.due_date)}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6C757D]">Currency</p>
                  <p className="font-medium text-[#2B2D42]">{invoice.currency || 'AED'}</p>
                </div>
                <div>
                  <p className="text-xs text-[#6C757D]">Status</p>
                  <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Items */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold text-[#2B2D42] mb-4">Invoice Items</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-sm font-semibold text-[#2B2D42]">Description</th>
                      <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Qty</th>
                      <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Unit Price</th>
                      <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Tax</th>
                      <th className="text-right py-2 text-sm font-semibold text-[#2B2D42]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoice.items || []).map((item, index) => {
                      const lineSub = Number(item.subtotal != null ? item.subtotal : (Number(item.quantity || 0) * Number(item.unit_price || 0)));
                      const lineDisc = Number(item.discount_amount != null ? item.discount_amount : (lineSub * (Number(item.discount || 0) / 100)));
                      return (
                        <tr key={index} className="border-b border-gray-100" data-testid={`invoice-item-row-${index}`}>
                          <td className="py-3 text-sm text-[#2B2D42]">
                            {item.description_en || item.description || item.product_name || 'N/A'}
                            {lineDisc > 0 && (
                              <div className="text-[11px] text-amber-700 mt-0.5">
                                Discount {Number(item.discount || 0)}% (− {formatCurrency(lineDisc, invoice.currency)})
                              </div>
                            )}
                          </td>
                          <td className="py-3 text-sm text-right text-[#6C757D]">{item.quantity}</td>
                          <td className="py-3 text-sm text-right text-[#6C757D]">{formatCurrency(item.unit_price, invoice.currency)}</td>
                          <td className="py-3 text-sm text-right text-[#6C757D]">{item.tax_rate || 0}%</td>
                          <td className="py-3 text-sm text-right font-medium text-[#2B2D42]">{formatCurrency(item.total, invoice.currency)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6C757D]">Subtotal:</span>
                    <span className="font-medium">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
                  </div>
                  {Number(invoice.discount_total || 0) > 0 && (
                    <div className="flex justify-between text-sm" data-testid="invoice-discount-row">
                      <span className="text-amber-700">Discount:</span>
                      <span className="font-medium text-amber-700">
                        − {formatCurrency(invoice.discount_total, invoice.currency)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-[#6C757D]">Tax:</span>
                    <span className="font-medium">{formatCurrency(invoice.tax_total, invoice.currency)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-200">
                    <span className="font-bold text-[#2B2D42]">Total:</span>
                    <span className="font-bold text-lg text-[#FF6B35]" data-testid="invoice-total">
                      {formatCurrency(invoice.total, invoice.currency)}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payments & Balance (installments) */}
          {!isDraft && !isProforma && (
            <Card data-testid="payments-card">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-[#2B2D42] flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-[#00B4D8]" />
                      Payments &amp; Balance
                    </h3>
                    <p className="text-xs text-[#6C757D] mt-0.5">
                      Record partial or full installments. The invoice closes automatically when the balance reaches zero.
                    </p>
                  </div>
                  {(() => {
                    const total = Number(invoice.total) || 0;
                    const paid = Number(invoice.amount_paid) || 0;
                    const balance = Math.max(0, Math.round(((invoice.balance_due != null ? Number(invoice.balance_due) : total - paid)) * 100) / 100);
                    const fullyPaid = balance <= 0.01 && total > 0;
                    return (
                      <Button
                        size="sm"
                        onClick={() => setPaymentModalOpen(true)}
                        disabled={fullyPaid}
                        className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
                        data-testid="record-payment-btn"
                      >
                        <Wallet className="w-4 h-4 mr-1" />
                        {fullyPaid ? 'Fully paid' : 'Record Payment'}
                      </Button>
                    );
                  })()}
                </div>

                {/* Balance bar */}
                {(() => {
                  const total = Number(invoice.total) || 0;
                  const paid = Number(invoice.amount_paid) || 0;
                  const balance = Math.max(0, Math.round(((invoice.balance_due != null ? Number(invoice.balance_due) : total - paid)) * 100) / 100);
                  const pct = total > 0 ? Math.min(100, (paid / total) * 100) : 0;
                  return (
                    <>
                      <div className="grid grid-cols-3 gap-3 mb-3 text-center">
                        <div className="bg-gray-50 rounded-lg p-2 border">
                          <div className="text-[10px] uppercase text-gray-500 tracking-wider">Total</div>
                          <div className="text-sm font-bold text-[#2B2D42]">{formatCurrency(total, invoice.currency)}</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2 border border-green-200">
                          <div className="text-[10px] uppercase text-green-700 tracking-wider">Paid</div>
                          <div className="text-sm font-bold text-green-700" data-testid="amount-paid-display">
                            {formatCurrency(paid, invoice.currency)}
                          </div>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-2 border border-amber-200">
                          <div className="text-[10px] uppercase text-amber-700 tracking-wider">Outstanding</div>
                          <div className="text-sm font-bold text-amber-700" data-testid="balance-due-display">
                            {formatCurrency(balance, invoice.currency)}
                          </div>
                        </div>
                      </div>
                      <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden mb-1">
                        <div
                          className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="text-[11px] text-gray-500 text-right">{pct.toFixed(1)}% paid</div>
                    </>
                  );
                })()}

                {/* Installments table */}
                <div className="mt-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                    Installment History ({payments.length})
                  </h4>
                  {paymentsLoading ? (
                    <div className="text-xs text-gray-500 py-4 text-center">Loading…</div>
                  ) : payments.length === 0 ? (
                    <div className="text-xs text-gray-500 py-4 text-center bg-gray-50 rounded-lg border border-dashed">
                      No payments recorded yet. Use <strong>Record Payment</strong> to register an installment.
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-600">
                          <tr>
                            <th className="px-3 py-2 text-left">#</th>
                            <th className="px-3 py-2 text-left">Date</th>
                            <th className="px-3 py-2 text-left">Method</th>
                            <th className="px-3 py-2 text-left">Reference</th>
                            <th className="px-3 py-2 text-right">Amount</th>
                            <th className="px-3 py-2 text-right" />
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {payments.map((p, idx) => (
                            <tr key={p.id} data-testid={`payment-row-${idx}`}>
                              <td className="px-3 py-2 text-xs text-gray-500">#{idx + 1}</td>
                              <td className="px-3 py-2 text-sm">{formatDate(p.payment_date)}</td>
                              <td className="px-3 py-2 text-sm capitalize">{(p.payment_method || '').replace('_', ' ')}</td>
                              <td className="px-3 py-2 text-xs text-gray-600">{p.reference || '—'}</td>
                              <td className="px-3 py-2 text-sm text-right font-semibold text-green-700">
                                {formatCurrency(p.amount, p.currency || invoice.currency)}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <div className="inline-flex gap-1">
                                  <a
                                    href={`${API_URL}/api/finance/payments/${p.id}/receipt.pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="p-1.5 rounded border bg-white hover:bg-cyan-50 text-[#00B4D8]"
                                    title="Download payment receipt (PDF for client)"
                                    data-testid={`payment-receipt-${idx}`}
                                  >
                                    <Receipt className="w-3.5 h-3.5" />
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => handleDeletePayment(p)}
                                    className="p-1.5 rounded border bg-white hover:bg-red-50 text-red-600"
                                    title="Reverse installment (recalculates balance)"
                                    data-testid={`payment-delete-${idx}`}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes & Terms */}
          {(invoice.terms || invoice.notes) && (
            <Card>
              <CardContent className="pt-6">
                {invoice.terms && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-[#2B2D42] mb-1">Payment Terms</h4>
                    <p className="text-sm text-[#6C757D]">{invoice.terms}</p>
                  </div>
                )}
                {invoice.notes && (
                  <div>
                    <h4 className="text-sm font-semibold text-[#2B2D42] mb-1">Notes</h4>
                    <p className="text-sm text-[#6C757D]">{invoice.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-semibold text-[#2B2D42] mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Button className="w-full justify-start" variant="outline" onClick={handlePreviewPDF} data-testid="qa-preview-pdf">
                  <Eye className="w-4 h-4 mr-2" /> Preview PDF
                </Button>
                <Button className="w-full justify-start" variant="outline" onClick={handleDownloadPDF} data-testid="qa-download-pdf">
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <div className="h-px bg-gray-200 my-3" />
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> Internal / Confidential
                </p>
                <Button
                  className="w-full justify-start border-amber-300 text-amber-800 hover:bg-amber-50"
                  variant="outline"
                  onClick={handlePreviewCostMargin}
                  data-testid="qa-preview-cost-margin"
                >
                  <FileBarChart className="w-4 h-4 mr-2" /> Preview Cost &amp; Margin
                </Button>
                <Button
                  className="w-full justify-start border-amber-300 text-amber-800 hover:bg-amber-50"
                  variant="outline"
                  onClick={handleDownloadCostMargin}
                  data-testid="qa-download-cost-margin"
                >
                  <Download className="w-4 h-4 mr-2" /> Download Cost &amp; Margin
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Issuer Company Summary */}
          {company.trading_name && (
            <Card className="border-[#FF6B35] border-2">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-[#2B2D42] mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#FF6B35]" /> Issuer Company
                </h3>
                <div className="text-sm space-y-1">
                  <p className="font-bold text-[#FF6B35]">{company.trading_name}</p>
                  <p className="text-[#6C757D]">{company.legal_name}</p>
                  <p className="text-[#6C757D]">{company.country}</p>
                  {company.license_number && <p className="text-[#6C757D]">License: {company.license_number}</p>}
                  {company.trn && <p className="text-[#6C757D]">TRN: {company.trn}</p>}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Record Payment modal */}
      <RecordPaymentModal
        isOpen={paymentModalOpen}
        invoice={invoice}
        onClose={() => setPaymentModalOpen(false)}
        onRecorded={async () => { await fetchInvoice(); await fetchPayments(); }}
      />
    </div>
  );
};

export default FinanceInvoiceDetail;
